import React, { useMemo, useState } from "react";
import addressData from "../address/address.json";

const buildRouteHomes = () => {
  const sections = Array.isArray(addressData?.sections) ? addressData.sections : [];
  let runningIndex = 1;

  return sections.flatMap((section) => {
    const street = section.street || "Unknown Street";
    const addresses = Array.isArray(section.addresses) ? section.addresses : [];

    return addresses.map((addressNumber) => {
      const paddedOrder = String(runningIndex).padStart(3, "0");
      runningIndex += 1;

      return {
        id: `route-home-${paddedOrder}`,
        order: paddedOrder,
        street,
        number: String(addressNumber),
        fullAddress: `${addressNumber} ${street}, San Diego, CA 92154`,
        defaultStatus: "Not at home",
        contacted: "No",
        contactedName: "",
        appointmentDate: "",
        appointmentTime: "",
        appointmentProduct: "",
        returnDate: "",
        returnTime: "",
        returnProduct: "",
        homeInsuranceExpires: "",
        autoInsuranceExpires: "",
        businessInsuranceExpires: "",
        doNotKnock: false,
        notes: "",
        imageUploads: [],
        videoUploads: [],
        videoComments: "",
      };
    });
  });
};

const routeHomes = buildRouteHomes();
const productOptions = ["", "Landscaping", "Insurance"];

function Routes() {
  const [streetSearch, setStreetSearch] = useState("");
  const [numberSearch, setNumberSearch] = useState("");
  const [expandedHomes, setExpandedHomes] = useState({});
  const [homesState, setHomesState] = useState(routeHomes);
  const [savingHomes, setSavingHomes] = useState({});
  const [saveMessages, setSaveMessages] = useState({});

  const filteredHomes = useMemo(() => {
    const normalizedStreet = streetSearch.trim().toLowerCase();
    const normalizedNumber = numberSearch.trim().toLowerCase();

    return homesState.filter((home) => {
      const streetMatches =
        !normalizedStreet || home.street.toLowerCase().includes(normalizedStreet);
      const numberMatches =
        !normalizedNumber || home.number.toLowerCase().includes(normalizedNumber);

      return streetMatches && numberMatches;
    });
  }, [streetSearch, numberSearch, homesState]);

  const groupedHomes = useMemo(() => {
    const groups = new Map();

    filteredHomes.forEach((home) => {
      if (!groups.has(home.street)) {
        groups.set(home.street, []);
      }
      groups.get(home.street).push(home);
    });

    return Array.from(groups.entries()).map(([street, homes]) => ({
      street,
      homes,
    }));
  }, [filteredHomes]);

  const toggleHomeExpanded = (homeId) => {
    setExpandedHomes((previous) => ({
      ...previous,
      [homeId]: !previous[homeId],
    }));
  };

  const updateHomeField = (homeId, field, value) => {
    setHomesState((previous) =>
      previous.map((home) => {
        if (home.id !== homeId) {
          return home;
        }

        const updatedHome = {
          ...home,
          [field]: value,
        };

        if (field === "contacted") {
          updatedHome.defaultStatus = value === "Yes" ? "Contacted" : "Not at home";
          if (value !== "Yes") {
            updatedHome.contactedName = "";
          }
        }

        if (field === "doNotKnock") {
          updatedHome.doNotKnock = Boolean(value);
        }

        return updatedHome;
      })
    );
  };

  const handleFileChange = (homeId, field, fileList) => {
    const files = Array.from(fileList || []).map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    updateHomeField(homeId, field, files);
  };

  const saveHomeRecord = async (home) => {
    setSavingHomes((previous) => ({
      ...previous,
      [home.id]: true,
    }));

    setSaveMessages((previous) => ({
      ...previous,
      [home.id]: "",
    }));

    try {
      const response = await fetch("/api/routes/save-home.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(home),
      });

      if (!response.ok) {
        throw new Error("Save request failed");
      }

      setSaveMessages((previous) => ({
        ...previous,
        [home.id]: "Saved for backend sync",
      }));
    } catch (error) {
      console.error("Save failed:", error);
      setSaveMessages((previous) => ({
        ...previous,
        [home.id]: "Could not save to backend yet",
      }));
    } finally {
      setSavingHomes((previous) => ({
        ...previous,
        [home.id]: false,
      }));
    }
  };

  return (
    <section style={styles.shell}>
      <div style={styles.header}>
        <div>
          <div style={styles.kicker}>Route directory</div>
          <h2 style={styles.title}>Organized route homes</h2>
          <p style={styles.copy}>
            Pulled from <code style={styles.inlineCode}>src/address/address.json</code> in route order.
            Search by street name or house number and review the default canvassing fields for each home.
          </p>
        </div>

        <div style={styles.routeBadge}>{addressData?.route || "Route"}</div>
      </div>

      <div style={styles.searchRow}>
        <div style={styles.searchField}>
          <label style={styles.label} htmlFor="routeStreetSearch">
            Street name
          </label>
          <input
            id="routeStreetSearch"
            type="text"
            value={streetSearch}
            onChange={(event) => setStreetSearch(event.target.value)}
            placeholder="Search street"
            style={styles.input}
          />
        </div>

        <div style={styles.searchFieldSmall}>
          <label style={styles.label} htmlFor="routeNumberSearch">
            House number
          </label>
          <input
            id="routeNumberSearch"
            type="text"
            value={numberSearch}
            onChange={(event) => setNumberSearch(event.target.value)}
            placeholder="Search number"
            style={styles.input}
          />
        </div>
      </div>

      <div style={styles.summaryRow}>
        <span style={styles.summaryPill}>Sections: {groupedHomes.length}</span>
        <span style={styles.summaryPill}>Visible homes: {filteredHomes.length}</span>
      </div>

      <div style={styles.sectionStack}>
        {groupedHomes.map((group) => (
          <div key={group.street} style={styles.streetSection}>
            <div style={styles.streetHeader}>
              <div>
                <div style={styles.streetTitle}>{group.street}</div>
                <div style={styles.streetMeta}>{group.homes.length} homes in this section</div>
              </div>
            </div>

            <div style={styles.cardGrid}>
              {group.homes.map((home) => {
                const isExpanded = Boolean(expandedHomes[home.id]);

                return (
                  <article key={home.id} style={styles.card}>
                    <button
                      type="button"
                      onClick={() => toggleHomeExpanded(home.id)}
                      style={styles.cardToggle}
                    >
                      <div style={styles.cardTop}>
                        <div>
                          <div style={styles.orderLine}>#{home.order}</div>
                          <div style={styles.addressLine}>{home.number} {home.street}</div>
                          <div style={styles.cityLine}>San Diego, CA 92154</div>
                        </div>

                        <div style={styles.cardTopRight}>
                          <div style={styles.statusPill}>{home.defaultStatus}</div>
                          <div style={styles.expandPill}>{isExpanded ? "Hide details" : "Show details"}</div>
                        </div>
                      </div>
                    </button>

                    {isExpanded ? (
                      <div style={styles.detailGrid}>
                        <div style={styles.detailItemForm}>
                          <label style={styles.formLabel}>Contacted</label>
                          <select
                            value={home.contacted}
                            onChange={(event) => updateHomeField(home.id, "contacted", event.target.value)}
                            style={styles.formControl}
                          >
                            <option value="No">No</option>
                            <option value="Yes">Yes</option>
                          </select>
                        </div>

                        {home.contacted === "Yes" ? (
                          <div style={styles.detailItemForm}>
                            <label style={styles.formLabel}>Name</label>
                            <input
                              type="text"
                              value={home.contactedName}
                              onChange={(event) => updateHomeField(home.id, "contactedName", event.target.value)}
                              placeholder="Name or leave blank"
                              style={styles.formControl}
                            />
                          </div>
                        ) : null}

                        <div style={styles.detailItemFormTriple}>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Appointment date</label>
                            <input
                              type="date"
                              value={home.appointmentDate}
                              onChange={(event) => updateHomeField(home.id, "appointmentDate", event.target.value)}
                              style={styles.formControl}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Appointment time</label>
                            <input
                              type="time"
                              value={home.appointmentTime}
                              onChange={(event) => updateHomeField(home.id, "appointmentTime", event.target.value)}
                              style={styles.formControl}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Appointment product</label>
                            <select
                              value={home.appointmentProduct}
                              onChange={(event) => updateHomeField(home.id, "appointmentProduct", event.target.value)}
                              style={styles.formControl}
                            >
                              {productOptions.map((option) => (
                                <option key={option || "empty-appointment-product"} value={option}>
                                  {option || "Select product"}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div style={styles.detailItemFormTriple}>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Return date</label>
                            <input
                              type="date"
                              value={home.returnDate}
                              onChange={(event) => updateHomeField(home.id, "returnDate", event.target.value)}
                              style={styles.formControl}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Return time</label>
                            <input
                              type="time"
                              value={home.returnTime}
                              onChange={(event) => updateHomeField(home.id, "returnTime", event.target.value)}
                              style={styles.formControl}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Return product</label>
                            <select
                              value={home.returnProduct}
                              onChange={(event) => updateHomeField(home.id, "returnProduct", event.target.value)}
                              style={styles.formControl}
                            >
                              {productOptions.map((option) => (
                                <option key={option || "empty-return-product"} value={option}>
                                  {option || "Select product"}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div style={styles.detailItemFormTriple}>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Home insurance expires</label>
                            <input
                              type="date"
                              value={home.homeInsuranceExpires}
                              onChange={(event) => updateHomeField(home.id, "homeInsuranceExpires", event.target.value)}
                              style={styles.formControl}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Auto insurance expires</label>
                            <input
                              type="date"
                              value={home.autoInsuranceExpires}
                              onChange={(event) => updateHomeField(home.id, "autoInsuranceExpires", event.target.value)}
                              style={styles.formControl}
                            />
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Business insurance expires</label>
                            <input
                              type="date"
                              value={home.businessInsuranceExpires}
                              onChange={(event) => updateHomeField(home.id, "businessInsuranceExpires", event.target.value)}
                              style={styles.formControl}
                            />
                          </div>
                        </div>

                        <div style={styles.detailItemForm}>
                          <label style={styles.formCheckboxLabel}>
                            <input
                              type="checkbox"
                              checked={Boolean(home.doNotKnock)}
                              onChange={(event) => updateHomeField(home.id, "doNotKnock", event.target.checked)}
                            />
                            <span style={styles.checkboxText}>Do not knock active</span>
                          </label>
                        </div>

                        <div style={styles.detailItemForm}>
                          <label style={styles.formLabel}>Notes</label>
                          <textarea
                            value={home.notes}
                            onChange={(event) => updateHomeField(home.id, "notes", event.target.value)}
                            placeholder="Add notes for this address"
                            style={styles.textareaControl}
                          />
                        </div>

                        <div style={styles.detailItemFormDouble}>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Upload images</label>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(event) => handleFileChange(home.id, "imageUploads", event.target.files)}
                              style={styles.fileControl}
                            />
                            <div style={styles.uploadMeta}>
                              {home.imageUploads.length ? `${home.imageUploads.length} image file(s)` : "No image files selected"}
                            </div>
                          </div>
                          <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Upload videos</label>
                            <input
                              type="file"
                              accept="video/*"
                              multiple
                              onChange={(event) => handleFileChange(home.id, "videoUploads", event.target.files)}
                              style={styles.fileControl}
                            />
                            <div style={styles.uploadMeta}>
                              {home.videoUploads.length ? `${home.videoUploads.length} video file(s)` : "No video files selected"}
                            </div>
                          </div>
                        </div>

                        <div style={styles.detailItemForm}>
                          <label style={styles.formLabel}>Video / image comments</label>
                          <textarea
                            value={home.videoComments}
                            onChange={(event) => updateHomeField(home.id, "videoComments", event.target.value)}
                            placeholder="Comments about uploaded images or videos"
                            style={styles.textareaControl}
                          />
                        </div>

                        <div style={styles.actionsRow}>
                          <button
                            type="button"
                            onClick={() => saveHomeRecord(home)}
                            style={styles.saveButton}
                          >
                            {savingHomes[home.id] ? "Saving..." : "Save record"}
                          </button>
                          <div style={styles.saveMessage}>{saveMessages[home.id] || "Ready to POST to PHP backend later"}</div>
                        </div>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </div>
        ))}

        {!groupedHomes.length ? (
          <div style={styles.emptyState}>
            No route homes matched your search. Try a different street name or house number.
          </div>
        ) : null}
      </div>
    </section>
  );
}

const styles = {
  shell: {
    marginTop: 28,
    padding: 28,
    borderRadius: 24,
    background: "linear-gradient(180deg, rgba(14, 14, 14, 0.78), rgba(6, 6, 6, 0.86))",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    boxShadow: "0 18px 60px rgba(0, 0, 0, 0.45)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 20,
    flexWrap: "wrap",
  },
  kicker: {
    display: "inline-block",
    marginBottom: 10,
    color: "#ffb347",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  title: {
    margin: 0,
    fontSize: "2rem",
    lineHeight: 1.05,
    color: "#ffffff",
  },
  copy: {
    margin: "10px 0 0",
    maxWidth: 780,
    color: "rgba(255,255,255,0.72)",
    lineHeight: 1.75,
  },
  inlineCode: {
    color: "#ffd166",
    background: "rgba(255,255,255,0.04)",
    padding: "2px 6px",
    borderRadius: 8,
  },
  routeBadge: {
    padding: "12px 18px",
    borderRadius: 999,
    border: "1px solid rgba(255, 177, 71, 0.24)",
    background: "rgba(255, 140, 26, 0.08)",
    color: "#ffd166",
    fontWeight: 700,
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  searchRow: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    marginTop: 24,
  },
  searchField: {
    minWidth: 260,
    flex: "1 1 340px",
  },
  searchFieldSmall: {
    minWidth: 180,
    flex: "0 1 220px",
  },
  label: {
    display: "block",
    marginBottom: 8,
    color: "rgba(255,255,255,0.62)",
    fontSize: "0.82rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.04)",
    color: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    appearance: "none",
  },
  summaryRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 20,
  },
  summaryPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.82)",
    fontSize: "0.82rem",
    fontWeight: 700,
  },
  sectionStack: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
    marginTop: 22,
  },
  streetSection: {
    padding: 18,
    borderRadius: 20,
    background: "linear-gradient(180deg, rgba(14, 14, 14, 0.72), rgba(7, 7, 7, 0.72))",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  streetHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 14,
  },
  streetTitle: {
    color: "#ffffff",
    fontSize: "1.12rem",
    fontWeight: 700,
  },
  streetMeta: {
    color: "rgba(255,255,255,0.62)",
    marginTop: 4,
  },
  cardGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  card: {
    padding: 18,
    borderRadius: 18,
    background: "linear-gradient(180deg, rgba(14, 14, 14, 0.82), rgba(7, 7, 7, 0.74))",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 10px 24px rgba(0,0,0,0.24)",
  },
  cardToggle: {
    width: "100%",
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    textAlign: "left",
  },
  cardTopRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  expandPill: {
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.82)",
    whiteSpace: "nowrap",
    fontWeight: 700,
    fontSize: "0.78rem",
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
  },
  orderLine: {
    color: "#ffb347",
    fontWeight: 700,
    letterSpacing: "0.04em",
    marginBottom: 6,
    fontSize: "0.88rem",
  },
  addressLine: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1rem",
  },
  cityLine: {
    color: "rgba(255,255,255,0.62)",
    marginTop: 6,
  },
  statusPill: {
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(255, 140, 26, 0.08)",
    border: "1px solid rgba(255, 177, 71, 0.2)",
    color: "#ffd166",
    whiteSpace: "nowrap",
    fontWeight: 700,
    fontSize: "0.82rem",
  },
  detailGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 16,
    paddingTop: 16,
  },
  detailItemForm: {
    padding: 14,
    borderRadius: 14,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  detailItemFormDouble: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
  },
  detailItemFormTriple: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 12,
  },
  formGroup: {
    padding: 14,
    borderRadius: 14,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  formLabel: {
    color: "rgba(255,255,255,0.62)",
    fontSize: "0.78rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  formControl: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.04)",
    color: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    appearance: "none",
  },
  textareaControl: {
    width: "100%",
    minHeight: 110,
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.04)",
    color: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    resize: "vertical",
    fontFamily: "inherit",
  },
  fileControl: {
    width: "100%",
    color: "rgba(255,255,255,0.82)",
  },
  uploadMeta: {
    color: "rgba(255,255,255,0.58)",
    fontSize: "0.82rem",
  },
  formCheckboxLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    color: "#ffffff",
    fontWeight: 600,
  },
  checkboxText: {
    color: "#ffffff",
  },
  actionsRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingTop: 4,
  },
  saveButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 16px",
    borderRadius: 14,
    border: "1px solid rgba(255,177,71,0.24)",
    background: "linear-gradient(135deg, #ff8c1a 0%, #ffd166 100%)",
    color: "#181818",
    fontWeight: 700,
    cursor: "pointer",
  },
  saveMessage: {
    color: "rgba(255,255,255,0.68)",
    fontSize: "0.88rem",
  },
  detailItem: {
    padding: 12,
    borderRadius: 14,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  detailLabel: {
    color: "rgba(255,255,255,0.5)",
    fontSize: "0.74rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    minWidth: 140,
  },
  detailValue: {
    color: "#ffffff",
    fontWeight: 600,
    lineHeight: 1.45,
    textAlign: "right",
    flex: 1,
  },
  emptyState: {
    padding: 18,
    borderRadius: 18,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.72)",
  },
};

export default Routes;